import "./App.css";
import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Dashboard from "./component/Dashboard";
import Introduction from "./component/Introduction";
import Body from "./component/Body";

import { Outlet, useLocation, Navigate } from "react-router-dom"; 
import SessionTimeout from "./component/SessionTimeout";
import MissedCallCounter from "./MissedCallCounter";
import LoadingOverlay from './component/LoadingOverlay';
import { Provider } from 'react-redux';
import store from './redux/store';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { showLoading, hideLoading } from './redux/slices/loadingSlice';
import { useDispatch } from 'react-redux';
import { extractEmailFromJwt } from './utils/jwtUtils';
import allowedUsers from './userlist';

// Define WhatsApp marketing related paths
const whatsappRelatedPaths = ['/templates', '/campaign', '/contacts'];

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [showIntroduction, setShowIntroduction] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); 
  const dispatch = useDispatch();
  //const timeoutDuration = parseInt(process.env.REACT_APP_TIMEOUT_DURATION);
  const timeoutDuration = 2 * 60 * 60 * 1000;
  
  // Check if current path is related to WhatsApp marketing
  const isWhatsAppMarketingRoute = whatsappRelatedPaths.some(path => 
    location.pathname.includes(path)
  );
  
  const handleLogout = useCallback(() => {
    dispatch(showLoading())
    setTimeout(() => {
    setIsLoggedIn(false);
    setUserEmail(null);
    setShowIntroduction(false);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("lastActivity");
    localStorage.removeItem("userEmail");
    localStorage.clear();
    dispatch(hideLoading()); 
    toast.success("Successfully signed out!"); 
    navigate("/");
  }, 1000);
  }, [navigate]);

  useEffect(() => {
    const roleReceived = localStorage.getItem("roleReceived");
   console.log( "roletoBeCirculated",roleReceived);
  })
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const storedEmail = localStorage.getItem("userEmail");
    
    if (accessToken) {
      setIsLoggedIn(true);
      
      // Restore user email state
      if (storedEmail) {
        setUserEmail(storedEmail);
        // Check if user still has access
        const userHasAccess = allowedUsers.includes(storedEmail.toLowerCase());
        if (!userHasAccess) {
          // If user no longer has access, show toast and logout
          toast.error("You don't have access to this portal. Please use the new portal");
          handleLogout();
          return;
        }
      }
      
      const lastActivity = localStorage.getItem("lastActivity");
      const currentTime = new Date().getTime();
      if (lastActivity && currentTime - lastActivity > timeoutDuration) {
        handleLogout();
      } else {
        localStorage.setItem("lastActivity", currentTime);
      }
    } else {
      navigate("/");
    }
  }, [handleLogout, navigate, timeoutDuration]);

  useEffect(() => {
    if (isLoggedIn && location.pathname === "/") {
      navigate("/introduction");
    }
    if (!isLoggedIn && location.pathname !== "/") {
      navigate("/");
    }
  }, [isLoggedIn, location.pathname, navigate]);

  useEffect(() => {
    localStorage.setItem("currentPath", location.pathname); 
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = localStorage.getItem("currentPath");
    if (currentPath) {
      navigate(currentPath); 
    }
  }, [navigate]);
  const onLoginSuccess = async (accessToken) => {
    try {
      if (!accessToken) {
        setLoginError("Unauthorized access. Please contact support.");
        return;
      }
      
      // Extract email from Google JWT token
      const email = extractEmailFromJwt(accessToken);
      console.log("User email from Google:", email);
      setUserEmail(email);
      
      // Check if user is in the allowed list
      const userHasAccess = email && allowedUsers.includes(email.toLowerCase());
      
      if (!userHasAccess) {
        console.log("User access denied:", email);
        // Show toast message and logout
        toast.error("You don't have access to this portal. Please use the new portal");
        setTimeout(() => {
          handleLogout();
        }, 1000); // Give time for user to read the toast
        return;
      }
      
      dispatch(showLoading())
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}token/${process.env.REACT_APP_CODED_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.credentials) {
          localStorage.setItem("accessToken", data.credentials);
          localStorage.setItem("clientId", data.clientId);
          localStorage.setItem("userEmail", email);
          //console.log('clientId', data.clientId);
          localStorage.setItem("roleReceived", data.role);
          console.log("roleReceivedFrombackend",data.role)
          setIsLoggedIn(true);
          setLoginError(null);
          setShowIntroduction(true);
          navigate("/introduction");
        }
      } else {
        const errorMessage = await response.text();
        setLoginError("Unauthorized access. Please contact support.");
      }
    } catch (error) {
      setLoginError("Unauthorized access. Please contact support.");
    }finally {
      setTimeout(() => {
        dispatch(hideLoading());
      },1000);
    }
  };
  const onLoginFailure = (error) => {
    setLoginError("Unauthorized access. Please contact support.");
  };
  const logout = () => {
    handleLogout();
  };
  const hideIntroduction = () => {
    setShowIntroduction(false);
  };
  if (!isLoggedIn && location.pathname !== '/') {
    return <Navigate to="/" />;
  }
  return (
    <Provider store={store}>
      <LoadingOverlay />
    <div className="App">
      <>
        <Header 
          isLoggedIn={isLoggedIn} 
          onLoginSuccess={onLoginSuccess}
          onLoginFailure={onLoginFailure}
          logout={logout}
          loginError={loginError}
        />
        {isLoggedIn && !isWhatsAppMarketingRoute && <MissedCallCounter handleLogout={handleLogout} />}
        <div className="main-content">
        <div className="connect-cont">
          {isLoggedIn ? (
            <Dashboard hideIntroduction={hideIntroduction} />
          ) : (
            <Body />
          )}
          <Outlet />
        </div>
      </div>
        <Footer />
        {/* {showIntroduction && <Introduction />} */}
        <SessionTimeout
          isLoggedIn={isLoggedIn}
          handleLogout={handleLogout}
          timeoutDuration={timeoutDuration}
        >
          {!isLoggedIn && <Navigate to="/" />}
        </SessionTimeout>
       <ToastContainer />
      </>
    </div>
    </Provider>
  
  );
}
export default App;
